import {
  CmsContentAboutStorySchema,
  type CmsContentAboutStoryForm,
} from "@/modules/cms/domain/contracts";
import { uploadImageToKey } from "@/shared/lib/upload-image-to-key";
import { ImageKeyUploadField } from "@/shared/ui/ImageKeyUploadField";
import {
  Button,
  FormCheckbox,
  FormInput,
  FormRoot,
  FormTextarea,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { useCallback, useState } from "react";
import type { Resolver, UseFormReturn } from "react-hook-form";

const IMAGE_KEY_PREFIX = "uploads/cms/about-story";

type AboutStoryFormProps = {
  content: CmsContentAboutStoryForm | null;
  onSubmit: (content: CmsContentAboutStoryForm) => void;
  onCancel: () => void;
  submitting?: boolean;
};

const defaultContent: CmsContentAboutStoryForm = {
  imageKey: "",
  title: {},
  content: {},
  isActive: true,
};

export function AboutStoryForm({
  content,
  onSubmit,
  onCancel,
  submitting,
}: AboutStoryFormProps) {
  const methods = RHF.useForm<CmsContentAboutStoryForm>({
    resolver: zodResolver(
      CmsContentAboutStorySchema,
    ) as Resolver<CmsContentAboutStoryForm>,
    defaultValues: content ?? defaultContent,
  });
  const watchImageKey = methods.watch("imageKey") ?? "";
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const handleSubmit = useCallback(
    async (vals: CmsContentAboutStoryForm) => {
      const data = { ...vals };
      if (pendingFile) {
        setUploading(true);
        try {
          data.imageKey = await uploadImageToKey(IMAGE_KEY_PREFIX, pendingFile);
        } finally {
          setUploading(false);
        }
      }
      onSubmit(data);
    },
    [pendingFile, onSubmit],
  );

  return (
    <FormRoot<CmsContentAboutStoryForm>
      methods={methods as UseFormReturn<CmsContentAboutStoryForm>}
      onSubmit={handleSubmit}
    >
      <div className="space-y-6">
        <ImageKeyUploadField
          keyPrefix={IMAGE_KEY_PREFIX}
          label="ຮູບ (4:3)"
          aspectHint="ອັດສ່ວນ 4:3"
          aspectRatio="aspect-[4/3]"
          widthPx={800}
          heightPx={600}
          value={watchImageKey}
          onChange={(key) =>
            methods.setValue("imageKey", key, { shouldDirty: true })
          }
          disabled={submitting}
          deferUpload
          pendingFile={pendingFile}
          onFileSelect={setPendingFile}
          onClear={() => {
            methods.setValue("imageKey", "", { shouldDirty: true });
            setPendingFile(null);
          }}
        />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <FormInput name="title.lo" label="ຫົວຂໍ້ (ລາວ)" />
          <FormInput name="title.th" label="ຫົວຂໍ້ (ໄທ)" />
          <FormInput name="title.en" label="ຫົວຂໍ້ (EN)" />
        </div>
        <div className="space-y-2">
          <span className="font-medium text-sm">ເນື້ອຫາ (HTML)</span>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <FormTextarea
              name="content.lo"
              label="ລາວ"
              rows={4}
              className="font-mono text-sm"
            />
            <FormTextarea
              name="content.th"
              label="ໄທ"
              rows={4}
              className="font-mono text-sm"
            />
            <FormTextarea
              name="content.en"
              label="EN"
              rows={4}
              className="font-mono text-sm"
            />
          </div>
        </div>
        <FormCheckbox name="isActive" label="ເປີດໃຊ້ງານ" />
        <div className="flex gap-2 pt-2">
          <Button type="submit" disabled={submitting || uploading}>
            {uploading ? "ກຳລັງອັບໂຫຼດ..." : submitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            ຍົກເລີກ
          </Button>
        </div>
      </div>
    </FormRoot>
  );
}
